// client/src/components/pages/PeriodSummary/index.js
//
// Period Summary (ST-00174) — tickets in a date range, summarised by Ref No or
// Order No. One row per key value, expandable into product / route / day /
// haulier breakdowns and the underlying tickets.
//
// Conventions followed (see CLAUDE.md → "Client UI conventions"):
//   - dates come from the shared UnifiedDateRangePicker and go out through
//     toApiDateString(); never a native <input type="date">
//   - colours are token-backed Tailwind utilities (bg-surface, text-slate-800,
//     text-gray-500 …) so the page re-themes in dark mode with no extra work
//   - recharts / inline-SVG colours come from utils (SVG attrs can't read var(--…))
//
// UX pass notes:
//   - the toolbar is a deliberate two-tier card (period on top, dimensions
//     below) instead of one flex row that wrapped Export onto a line of its own
//   - `from/to/by/type/view` live in the query string, so a view is shareable
//     and survives a reload; the URL wins over the localStorage preferences
//   - the headline variance is re-summed over MATCHED keys only — see
//     utils.matchedVariance for why the API total is the wrong number to show

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '../../common/PageHeader';
import { useSearchParams } from 'react-router-dom';
import { FaCalendarAlt, FaSearch, FaSyncAlt, FaFileCsv, FaExclamationCircle } from 'react-icons/fa';
import UnifiedDateRangePicker from '../../common/UnifiedDateRangePicker';
import LoadingSpinner from '../../common/LoadingSpinner';
import { toApiDateString } from '../../../utils/dateUtils';
import {
  getPeriodSummary,
  getPeriodSummaryBreakdown,
  getPeriodSummaryTimeline,
  getPeriodSummaryGroupings,
} from '../../../services/periodSummaryService';
import SummaryTable from './SummaryTable';
import TimelineView from './TimelineView';
import RoutePicker, {
  ALL_ROUTES,
  routeFilterOf,
  routeOptionId,
  routeOptionLabel,
} from './RoutePicker';
import {
  GROUP_BY_OPTIONS,
  VIEW_OPTIONS,
  TICKET_TYPE_OPTIONS,
  DATE_PRESETS,
  DEFAULT_DAYS,
  activePresetId,
  groupByLabel,
  formatCount,
  formatTonnes,
  formatTonnesCompact,
  formatPct,
  matchedVariance,
  buildSparklineMap,
  varianceToneClass,
  compareValues,
  groupSortValue,
  parseIsoDate,
  firstValidOption,
  buildCsv,
  downloadCsv,
  readPrefs,
  writePrefs,
} from './utils';

const SEARCH_DEBOUNCE_MS = 250;
// The table's sparklines come from the same /timeline endpoint the Timeline view
// uses; 200 is that endpoint's hard cap, so every row it can cover gets one.
const SPARKLINE_LIMIT = 200;

const defaultStartDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - (DEFAULT_DAYS - 1));
  return d;
};

// ── Small presentational pieces ───────────────────────────────────────────────

/**
 * A segmented control. Exposed as a real radiogroup (not a bag of toggle
 * buttons) so a screen reader announces "2 of 3" and the arrow keys move the
 * selection the way they do in every other radio group.
 */
const SegmentedToggle = ({ label, options, value, onChange, size = 'md' }) => {
  const pad = size === 'sm' ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs';

  const handleKeyDown = (event) => {
    const delta =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (!delta) return;
    event.preventDefault();
    const idx = options.findIndex((o) => o.value === value);
    const next = options[(idx + delta + options.length) % options.length];
    onChange(next.value);
  };

  return (
    <div
      className="flex items-center bg-gray-100 rounded-lg p-0.5"
      role="radiogroup"
      aria-label={label}
      onKeyDown={handleKeyDown}
    >
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value || 'all'}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(opt.value)}
            className={`${pad} rounded-md font-medium transition-all duration-200 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/50 ${
              selected ? 'bg-surface text-slate-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

/** Icon-only toolbar action. The label is the tooltip AND the accessible name. */
const IconButton = ({ label, onClick, disabled, children, spinning }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
    className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 bg-surface text-slate-800 hover:bg-gray-50 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/50 ${
      spinning ? 'animate-spin' : ''
    }`}
  >
    {children}
  </button>
);

const KpiTile = ({ label, value, sub, foot, tone = 'text-slate-800', title }) => (
  <div
    className="bg-surface rounded-lg border border-gray-200 shadow-sm px-3 py-2.5 min-w-0"
    title={title}
  >
    <div className="text-[10px] uppercase tracking-wide text-gray-400 truncate">{label}</div>
    <div className={`text-lg font-bold font-mono tabular-nums truncate ${tone}`}>{value}</div>
    {/* No `truncate` here on purpose — the sub-caption carries the split that
        explains the number ("34,149 dispatch · 30,097 receipt"), so it wraps. */}
    {sub && <div className="text-[10px] text-gray-500 leading-snug break-words">{sub}</div>}
    {foot && <div className="text-[10px] text-gray-400 leading-snug break-words">{foot}</div>}
  </div>
);

const KpiSkeleton = ({ count }) => (
  <div className="animate-pulse grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="bg-surface rounded-lg border border-gray-200 px-3 py-2.5">
        <div className="h-2.5 bg-gray-200 rounded w-2/3 mb-2" />
        <div className="h-5 bg-gray-100 rounded w-1/2" />
      </div>
    ))}
  </div>
);

const TableSkeleton = () => (
  <div className="animate-pulse bg-surface rounded-lg border border-gray-200 overflow-hidden">
    <div className="h-9 bg-gray-100" />
    {[...Array(8)].map((_, i) => (
      <div key={i} className="flex items-center gap-3 px-4 py-3 border-t border-gray-100">
        <div className="h-3 bg-gray-200 rounded w-1/5" />
        <div className="h-3 bg-gray-100 rounded w-1/12" />
        <div className="h-3 bg-gray-100 rounded w-1/12" />
        <div className="h-3 bg-gray-100 rounded w-1/12" />
        <div className="h-3 bg-gray-100 rounded flex-1" />
      </div>
    ))}
  </div>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const PeriodSummary = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read the URL ONCE. After mount the page owns the query string (it writes it
  // with `replace`), so re-reading it would fight the controls.
  const initial = useRef(null);
  if (initial.current === null) {
    const prefs = readPrefs();
    initial.current = {
      startDate: parseIsoDate(searchParams.get('from')) || defaultStartDate(),
      endDate: parseIsoDate(searchParams.get('to')) || new Date(),
      groupBy:
        firstValidOption(GROUP_BY_OPTIONS, searchParams.get('by'), prefs.groupBy) ?? 'ref_no',
      ticketType:
        firstValidOption(TICKET_TYPE_OPTIONS, searchParams.get('type'), prefs.ticketType) ?? '',
      view: firstValidOption(VIEW_OPTIONS, searchParams.get('view'), prefs.view) ?? 'table',
      // A route filter is a drill-in, not a preference: it round-trips through
      // the URL (so a filtered view is shareable) but is never restored from
      // localStorage. `g` is a Route Grouping id (the picker); `dl` / `rl` are
      // raw legs from "Filter to this route". `''` is a real location, so
      // presence is `has`, not truthiness.
      groupingId: /^\d+$/.test(searchParams.get('g') || '') ? Number(searchParams.get('g')) : null,
      dispatchLocation: searchParams.has('dl') ? searchParams.get('dl') : null,
      receiptLocation: searchParams.has('rl') ? searchParams.get('rl') : null,
    };
  }

  const [startDate, setStartDate] = useState(initial.current.startDate);
  const [endDate, setEndDate] = useState(initial.current.endDate);
  const [groupBy, setGroupBy] = useState(initial.current.groupBy);
  const [ticketType, setTicketType] = useState(initial.current.ticketType);
  const [view, setView] = useState(initial.current.view);
  const [routeFilter, setRouteFilter] = useState({
    groupingId: initial.current.groupingId,
    dispatchLocation: initial.current.dispatchLocation,
    receiptLocation: initial.current.receiptLocation,
  });

  const [groupings, setGroupings] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [routeCleared, setRouteCleared] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  // Daily activity behind the table's sparkline column. Deliberately a SECOND,
  // independent request: the table must paint as soon as the summary lands and
  // let the sparklines fill in, never wait for both.
  const [sparkTimeline, setSparkTimeline] = useState(null);

  const hasLoadedOnce = useRef(false);

  const [sort, setSort] = useState({ column: 'tickets', dir: 'desc' });
  const [expandedKey, setExpandedKey] = useState(null);
  const [breakdowns, setBreakdowns] = useState({});

  // The cache is mirrored in a ref because the "is this already loading?" check
  // has to be synchronous — a setState updater does not run before the handler
  // returns, so reading through state would fire duplicate requests.
  const breakdownsRef = useRef({});

  const startStr = toApiDateString(startDate);
  const endStr = toApiDateString(endDate);

  // Memoised so the child effects (which depend on the object) don't re-run on
  // every parent render.
  const query = useMemo(
    () => ({
      startDate: startStr,
      endDate: endStr,
      groupBy,
      ticketType: ticketType || undefined,
      // null (not undefined) so the service's `clean` drops them; an empty
      // STRING is a real location and is sent.
      groupingId: routeFilter.groupingId,
      dispatchLocation: routeFilter.dispatchLocation,
      receiptLocation: routeFilter.receiptLocation,
    }),
    [startStr, endStr, groupBy, ticketType, routeFilter]
  );

  const routeValue = routeOptionId(routeFilter);
  const routeFiltering = routeValue !== ALL_ROUTES;

  const singleType = ticketType === 'Dispatch' || ticketType === 'Receipt';
  const sparklineSide =
    ticketType === 'Dispatch' ? 'dispatch' : ticketType === 'Receipt' ? 'receipt' : 'both';

  // Persist the sticky controls…
  useEffect(() => {
    writePrefs({ ...readPrefs(), groupBy, ticketType, view });
  }, [groupBy, ticketType, view]);

  // …and mirror the shareable ones into the query string (replace: a period
  // tweak must not add a browser-history entry to back out of).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('from', startStr);
    next.set('to', endStr);
    next.set('by', groupBy);
    if (ticketType) next.set('type', ticketType);
    else next.delete('type');
    next.set('view', view);
    if (routeFilter.groupingId === null) next.delete('g');
    else next.set('g', String(routeFilter.groupingId));
    if (routeFilter.dispatchLocation === null) next.delete('dl');
    else next.set('dl', routeFilter.dispatchLocation);
    if (routeFilter.receiptLocation === null) next.delete('rl');
    else next.set('rl', routeFilter.receiptLocation);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [startStr, endStr, groupBy, ticketType, view, routeFilter, searchParams, setSearchParams]);

  // Debounced client-side search over the key.
  useEffect(() => {
    const id = setTimeout(() => setSearchTerm(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Main fetch. A change of period / grouping / ticket type invalidates the
  // expanded row and every cached breakdown.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setError(null);
      // First load gets a skeleton; every later fetch keeps the old table on
      // screen behind a subtle spinner.
      if (hasLoadedOnce.current) setRefreshing(true);
      else setLoading(true);

      try {
        const body = await getPeriodSummary(query);
        if (cancelled) return;
        setData(body);
        hasLoadedOnce.current = true;
      } catch (err) {
        if (cancelled) return;
        setError(
          err?.response?.data?.message || err?.message || 'Failed to load the period summary'
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    setExpandedKey(null);
    breakdownsRef.current = {};
    setBreakdowns({});
    run();

    return () => {
      cancelled = true;
    };
  }, [query, refreshToken]);

  // Sparkline data. Only in the table view — the Timeline view fetches the same
  // endpoint itself, with its own limit and search. A failure here is silent:
  // the column simply shows dashes rather than breaking a working table.
  useEffect(() => {
    if (view !== 'table') return undefined;
    let cancelled = false;

    setSparkTimeline(null);
    getPeriodSummaryTimeline({ ...query, limit: SPARKLINE_LIMIT })
      .then((body) => {
        if (!cancelled) setSparkTimeline(body);
      })
      .catch(() => {
        if (!cancelled) setSparkTimeline(null);
      });

    return () => {
      cancelled = true;
    };
  }, [query, refreshToken, view]);

  // Route options for the toolbar filter: the tenant's Route Groupings with
  // their counts for this period. They depend on the period and the ticket type
  // (and on groupBy, which the API uses to count distinct keys per route), never
  // on the route filter itself — otherwise picking a route would shrink the
  // list you picked from.
  useEffect(() => {
    let cancelled = false;
    setRoutesLoading(true);
    getPeriodSummaryGroupings({
      startDate: startStr,
      endDate: endStr,
      groupBy,
      ticketType: ticketType || undefined,
    })
      .then((body) => {
        if (cancelled) return;
        const rows = body?.groupings || [];
        setGroupings(rows);

        // A grouping that no longer exists (deleted, or another tenant's id in
        // a pasted URL) would silently filter everything away, so drop it and
        // say so rather than showing an empty table with a filter the user
        // cannot see the effect of. Raw legs are left alone: an empty result
        // for them is the honest answer.
        setRouteFilter((prev) => {
          if (prev.groupingId === null) return prev;
          if (rows.some((g) => Number(g.id) === prev.groupingId)) return prev;
          setRouteCleared(routeOptionLabel(routeOptionId(prev), rows));
          return { groupingId: null, dispatchLocation: null, receiptLocation: null };
        });
      })
      .catch(() => {
        if (!cancelled) setGroupings([]);
      })
      .finally(() => {
        if (!cancelled) setRoutesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [startStr, endStr, groupBy, ticketType, refreshToken]);

  const handleRouteChange = useCallback((optionId) => {
    setRouteCleared(null);
    setRouteFilter(routeFilterOf(optionId));
  }, []);

  /**
   * "Filter to this route" from a Route breakdown row: a Route Grouping when
   * the row is one, else the raw legs of tickets on no grouping.
   */
  const handleFilterToRoute = useCallback((route) => {
    setRouteCleared(null);
    const groupingId = Number(route?.groupingId);
    setRouteFilter(
      Number.isInteger(groupingId) && groupingId > 0
        ? { groupingId, dispatchLocation: null, receiptLocation: null }
        : {
            groupingId: null,
            dispatchLocation: route?.dispatchLocation ?? '',
            receiptLocation: route?.receiptLocation ?? '',
          }
    );
  }, []);

  const sparklines = useMemo(() => buildSparklineMap(sparkTimeline), [sparkTimeline]);
  const timelineDays = useMemo(() => sparkTimeline?.days || [], [sparkTimeline]);

  const loadBreakdown = useCallback(
    async (key, by, { force = false } = {}) => {
      const cacheKey = `${key}|${by}`;
      const existing = breakdownsRef.current[cacheKey];
      // Already loading, or cached (an errored entry only refetches on an
      // explicit Retry, which passes force).
      if (existing !== undefined && !force) return;
      if (existing === 'loading') return;

      const commit = (value) => {
        breakdownsRef.current = { ...breakdownsRef.current, [cacheKey]: value };
        setBreakdowns(breakdownsRef.current);
      };

      commit('loading');
      try {
        const body = await getPeriodSummaryBreakdown({ ...query, key, by });
        commit({ rows: body?.rows || [] });
      } catch (err) {
        commit({
          error: err?.response?.data?.message || err?.message || 'Failed to load breakdown',
        });
      }
    },
    [query]
  );

  const handleSortChange = useCallback((columnId) => {
    setSort((prev) =>
      prev.column === columnId
        ? { column: columnId, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { column: columnId, dir: columnId === 'key' ? 'asc' : 'desc' }
    );
  }, []);

  const handleToggleExpand = useCallback((key) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  }, []);

  const applyPreset = useCallback((preset) => {
    const { start, end } = preset.range();
    setStartDate(start);
    setEndDate(end);
  }, []);

  const totals = data?.totals || {};
  const allGroups = useMemo(() => data?.groups || [], [data]);

  const visibleGroups = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const filtered = term
      ? allGroups.filter((g) =>
          String(g.key || '')
            .toLowerCase()
            .includes(term)
        )
      : allGroups;
    return [...filtered].sort((a, b) =>
      compareValues(groupSortValue(a, sort.column), groupSortValue(b, sort.column), sort.dir)
    );
  }, [allGroups, searchTerm, sort]);

  const matched = useMemo(() => matchedVariance(allGroups), [allGroups]);

  const keyLabel = groupByLabel(groupBy);

  const handleExport = useCallback(() => {
    // The rows are already route-filtered server-side, so the export follows the
    // filter for free; the header names whatever the page is grouped by.
    const csv = buildCsv(visibleGroups, groupByLabel(groupBy));
    downloadCsv(`period-summary_${groupBy}_${startStr}_${endStr}.csv`, csv);
  }, [visibleGroups, groupBy, startStr, endStr]);
  const presetId = activePresetId(startStr, endStr, toApiDateString);
  const kpiCount = singleType ? 5 : 7;

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaCalendarAlt}
        title="Period Summary"
        subtitle="Tickets in a period, summarised by Ref No, Order No or Product"
        className="z-20"
      />

      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-4">
        {/* Toolbar — one card, two tiers: WHEN on top, HOW on the bottom. */}
        <div className="bg-surface rounded-lg border border-gray-200 shadow-sm p-2.5 space-y-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-full sm:w-60 flex-shrink-0">
              <UnifiedDateRangePicker
                startDate={startDate}
                onStartDateChange={setStartDate}
                endDate={endDate}
                onEndDateChange={setEndDate}
                bounds="past"
              />
            </div>

            <div
              className="flex flex-wrap items-center gap-1"
              role="group"
              aria-label="Date range presets"
            >
              {DATE_PRESETS.map((preset) => {
                const active = presetId === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    aria-pressed={active}
                    className={`px-2.5 py-1 rounded-full border text-[11px] font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/50 ${
                      active
                        ? 'border-primary-500 bg-primary-500/10 text-primary-700'
                        : 'border-gray-200 text-gray-500 hover:text-slate-800 hover:bg-gray-50'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2 ml-auto w-full sm:w-auto">
              <div className="relative flex-1 sm:w-48 sm:flex-none">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={`Search ${keyLabel}…`}
                  aria-label={`Search ${keyLabel}`}
                  className="w-full pl-9 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-surface text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-500"
                />
              </div>

              <IconButton
                label="Refresh"
                onClick={() => setRefreshToken((n) => n + 1)}
                disabled={loading || refreshing}
                spinning={refreshing}
              >
                <FaSyncAlt className="text-[11px]" />
              </IconButton>

              <IconButton
                label="Export CSV"
                onClick={handleExport}
                disabled={visibleGroups.length === 0}
              >
                <FaFileCsv className="text-[11px]" />
              </IconButton>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
            <SegmentedToggle
              label="View"
              options={VIEW_OPTIONS}
              value={view}
              onChange={setView}
              size="sm"
            />
            <span className="hidden sm:block w-px h-5 bg-gray-200" aria-hidden="true" />
            <SegmentedToggle
              label="Group by"
              options={GROUP_BY_OPTIONS}
              value={groupBy}
              onChange={setGroupBy}
              size="sm"
            />
            <SegmentedToggle
              label="Ticket type"
              options={TICKET_TYPE_OPTIONS}
              value={ticketType}
              onChange={setTicketType}
              size="sm"
            />

            <RoutePicker
              groupings={groupings}
              loading={routesLoading}
              value={routeValue}
              onChange={handleRouteChange}
            />

            <div className="ml-auto flex items-center gap-2 text-[11px] text-gray-400">
              {refreshing && (
                <span className="flex items-center gap-1.5">
                  <LoadingSpinner size="sm" /> Refreshing…
                </span>
              )}
              {!loading && data && (
                <span>
                  {formatCount(visibleGroups.length)} of {formatCount(totals.groups)} {keyLabel}
                  {searchTerm ? ' matching' : ''}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div
            role="alert"
            className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg flex items-center justify-between gap-3"
          >
            <span className="flex items-center gap-2">
              <FaExclamationCircle className="flex-shrink-0" />
              {error}
            </span>
            <button
              type="button"
              onClick={() => setRefreshToken((n) => n + 1)}
              className="px-2.5 py-1 rounded border border-red-300 text-red-700 hover:bg-red-100 font-medium"
            >
              Retry
            </button>
          </div>
        )}

        {/* Active route filter / "your route vanished" notice */}
        {(routeFiltering || routeCleared) && (
          <div className="flex flex-wrap items-center gap-2">
            {routeFiltering && (
              <span
                className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full border border-primary-500 bg-primary-500/10 text-primary-700 text-[11px] font-medium max-w-full"
                data-testid="route-filter-chip"
              >
                <span className="truncate">Route: {routeOptionLabel(routeValue, groupings)}</span>
                <button
                  type="button"
                  onClick={() => handleRouteChange(ALL_ROUTES)}
                  aria-label="Clear route filter"
                  title="Clear route filter"
                  className="w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-primary-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/50"
                >
                  ×
                </button>
              </span>
            )}
            {routeCleared && (
              <span className="text-[11px] text-amber-700">
                “{routeCleared}” has no tickets in this period — route filter cleared.
              </span>
            )}
          </div>
        )}

        {/* KPIs */}
        {loading ? (
          <KpiSkeleton count={kpiCount} />
        ) : (
          data && (
            <div
              className={`grid grid-cols-2 sm:grid-cols-3 gap-2 ${
                singleType ? 'lg:grid-cols-5' : 'lg:grid-cols-7'
              }`}
              data-testid="period-summary-kpis"
            >
              <KpiTile
                label="Groups"
                value={formatCount(totals.groups)}
                sub={routeFiltering ? 'on this route' : keyLabel}
              />
              <KpiTile
                label="Tickets"
                value={formatCount(totals.tickets)}
                sub={
                  singleType
                    ? `${ticketType.toLowerCase()} only`
                    : `${formatCount(totals.dispatchTickets)} dispatch · ${formatCount(
                        totals.receiptTickets
                      )} receipt`
                }
              />
              <KpiTile
                label="Nett tonnes"
                value={formatTonnesCompact(totals.nettTonnes)}
                sub={`${formatTonnes(totals.nettTonnes)} t`}
              />
              {ticketType !== 'Receipt' && (
                <KpiTile
                  label="Dispatch tonnes"
                  value={formatTonnesCompact(totals.dispatchTonnes)}
                  sub={`${formatTonnes(totals.dispatchTonnes)} t`}
                />
              )}
              {ticketType !== 'Dispatch' && (
                <KpiTile
                  label="Receipt tonnes"
                  value={formatTonnesCompact(totals.receiptTonnes)}
                  sub={`${formatTonnes(totals.receiptTonnes)} t`}
                />
              )}
              {!singleType && (
                <KpiTile
                  label="Variance (matched)"
                  value={
                    matched.varianceTonnes === null ? '—' : formatTonnes(matched.varianceTonnes)
                  }
                  tone={varianceToneClass(matched.variancePct)}
                  sub={
                    matched.variancePct === null
                      ? 'No key has both sides in this period'
                      : `${formatPct(matched.variancePct)} on ${formatCount(matched.matchedKeys)} matched ${keyLabel}`
                  }
                  foot={
                    matched.oneSidedKeys > 0
                      ? `${formatCount(matched.oneSidedKeys)} one-sided excluded`
                      : undefined
                  }
                  title="Receipt minus dispatch across only the keys that have BOTH a dispatch and a receipt in this period. One-sided keys (dispatched but not yet received, or vice versa) are excluded — counting them would read as a loss that never happened."
                />
              )}
              <KpiTile
                label="Unkeyed tickets"
                value={formatCount(totals.unkeyedTickets)}
                tone="text-gray-500"
                title={`Tickets with no ${keyLabel} in this period`}
                sub="no key"
              />
            </div>
          )
        )}

        {/* Body — Table or Timeline over the same period / grouping / type. The
            timeline owns its own fetch of /period-summary/timeline. */}
        {view === 'timeline' ? (
          <TimelineView
            query={query}
            searchTerm={searchTerm}
            breakdowns={breakdowns}
            onLoadBreakdown={loadBreakdown}
            refreshToken={refreshToken}
            onFilterRoute={handleFilterToRoute}
          />
        ) : loading ? (
          <TableSkeleton />
        ) : (
          data && (
            <SummaryTable
              groups={visibleGroups}
              totalGroups={totals.groups}
              truncated={Boolean(data.truncated)}
              groupBy={groupBy}
              showBothSides={!singleType}
              sparklineSide={sparklineSide}
              timelineDays={timelineDays}
              sparklines={sparklines}
              sort={sort}
              onSortChange={handleSortChange}
              expandedKey={expandedKey}
              onToggleExpand={handleToggleExpand}
              query={query}
              breakdowns={breakdowns}
              onLoadBreakdown={loadBreakdown}
              onFilterRoute={handleFilterToRoute}
              searchTerm={searchTerm}
            />
          )
        )}
      </div>
    </div>
  );
};

export default PeriodSummary;
